import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft, User, Mail, Calendar, CreditCard, Shield, MessageSquare,
  Activity, FileText, Loader2, Ban, CheckCircle, Trash2, Plus
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

export default function AdminUserDetail() {
  const { userId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [noteText, setNoteText] = useState('');
  const [savingNote, setSavingNote] = useState(false);

  const fetchUser = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/admin/users/${userId}`, { credentials: 'include' });
      if (!res.ok) throw new Error('Not found');
      setData(await res.json());
    } catch (e) {
      toast.error('Failed to load user');
      navigate('/admin/users');
    } finally {
      setLoading(false);
    }
  }, [userId, navigate]);

  useEffect(() => {
    fetchUser();
  }, [fetchUser]);

  const handleSuspend = async () => {
    if (!window.confirm('Suspend this user?')) return;
    try {
      await fetch(`${API}/api/admin/users/${userId}/suspend`, { method: 'POST', credentials: 'include' });
      toast.success('User suspended');
      fetchUser();
    } catch (e) { toast.error('Failed'); }
  };

  const handleUnsuspend = async () => {
    try {
      await fetch(`${API}/api/admin/users/${userId}/unsuspend`, { method: 'POST', credentials: 'include' });
      toast.success('User unsuspended');
      fetchUser();
    } catch (e) { toast.error('Failed'); }
  };

  const handleDelete = async () => {
    if (!window.confirm('PERMANENTLY delete this user and all their data?')) return;
    try {
      await fetch(`${API}/api/admin/users/${userId}`, { method: 'DELETE', credentials: 'include' });
      toast.success('User deleted');
      navigate('/admin/users');
    } catch (e) { toast.error('Failed'); }
  };

  const handleAddNote = async () => {
    if (!noteText.trim()) return;
    setSavingNote(true);
    try {
      await fetch(`${API}/api/admin/users/${userId}/note`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ note: noteText })
      });
      toast.success('Note added');
      setNoteText('');
      fetchUser();
    } catch (e) { toast.error('Failed'); }
    finally { setSavingNote(false); }
  };

  if (loading) {
    return <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 text-white/40 animate-spin" /></div>;
  }

  if (!data) return null;

  const { user, events, consent, tickets, feedback, notes, stats } = data;

  return (
    <div className="space-y-6" data-testid="admin-user-detail">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" onClick={() => navigate('/admin/users')} className="text-white/50 hover:text-white">
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div className="flex-1">
          <h1 className="text-2xl font-bold text-white font-space">{user.name || 'No Name'}</h1>
          <p className="text-white/40 text-sm">{user.email}</p>
        </div>
        <span className={`px-3 py-1 text-xs rounded-full border font-semibold ${
          user.status === 'active' ? 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30' :
          user.status === 'suspended' ? 'bg-red-500/20 text-red-400 border-red-500/30' :
          'bg-gray-500/20 text-gray-400 border-gray-500/30'
        }`} data-testid="user-status">
          {(user.status || 'active').toUpperCase()}
        </span>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        {[
          { label: 'PI Checks', value: stats.total_checks, icon: Activity, color: 'text-cyan-400' },
          { label: 'Accept Rate', value: `${stats.acceptance_rate}%`, icon: CheckCircle, color: 'text-emerald-400' },
          { label: 'Top Tone', value: stats.top_tone || 'N/A', icon: MessageSquare, color: 'text-purple-400' },
          { label: 'Top Intent', value: stats.top_intent || 'N/A', icon: FileText, color: 'text-amber-400' },
          { label: 'Credits Left', value: user.credits_remaining || 0, icon: CreditCard, color: 'text-cyan-400' },
        ].map(m => (
          <div key={m.label} className="bg-[#0E1830] border border-white/10 rounded-xl p-4">
            <m.icon className={`w-4 h-4 ${m.color} mb-2`} />
            <p className="text-white font-bold text-lg truncate">{m.value}</p>
            <p className="text-white/40 text-xs">{m.label}</p>
          </div>
        ))}
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* User Info */}
        <div className="bg-[#0E1830] border border-white/10 rounded-xl p-6 space-y-4">
          <h2 className="text-white font-medium">Profile</h2>
          {[
            { icon: User, label: 'Name', val: user.name },
            { icon: Mail, label: 'Email', val: user.email },
            { icon: Shield, label: 'Role', val: user.role },
            { icon: CreditCard, label: 'Plan', val: user.plan },
            { icon: Calendar, label: 'Joined', val: user.created_at ? new Date(user.created_at).toLocaleDateString() : 'N/A' },
          ].map(r => (
            <div key={r.label} className="flex items-center gap-3">
              <r.icon className="w-4 h-4 text-white/30" />
              <span className="text-white/40 text-sm w-16">{r.label}</span>
              <span className="text-white text-sm">{r.val || 'N/A'}</span>
            </div>
          ))}
          <div className="flex items-center gap-2 pt-2">
            <span className={`text-xs px-2 py-0.5 rounded-full ${user.consent_signed ? 'bg-emerald-500/20 text-emerald-400' : 'bg-red-500/20 text-red-400'}`}>
              Consent: {user.consent_signed ? 'Signed' : 'Not Signed'}
            </span>
            <span className={`text-xs px-2 py-0.5 rounded-full ${user.feedback_submitted ? 'bg-emerald-500/20 text-emerald-400' : 'bg-amber-500/20 text-amber-400'}`}>
              Feedback: {user.feedback_submitted ? 'Done' : 'Pending'}
            </span>
          </div>

          {/* Actions */}
          <div className="flex flex-wrap gap-2 pt-3 border-t border-white/5">
            {user.status === 'active' ? (
              <Button size="sm" variant="outline" onClick={handleSuspend}
                className="border-amber-500/30 text-amber-400 hover:bg-amber-500/10 text-xs" data-testid="suspend-btn">
                <Ban className="w-3 h-3 mr-1" /> Suspend
              </Button>
            ) : (
              <Button size="sm" variant="outline" onClick={handleUnsuspend}
                className="border-emerald-500/30 text-emerald-400 hover:bg-emerald-500/10 text-xs" data-testid="unsuspend-btn">
                <CheckCircle className="w-3 h-3 mr-1" /> Unsuspend
              </Button>
            )}
            <Button size="sm" variant="outline" onClick={handleDelete}
              className="border-red-500/30 text-red-400 hover:bg-red-500/10 text-xs" data-testid="delete-user-btn">
              <Trash2 className="w-3 h-3 mr-1" /> Delete
            </Button>
          </div>
        </div>

        {/* Consent */}
        <div className="bg-[#0E1830] border border-white/10 rounded-xl p-6">
          <h2 className="text-white font-medium mb-4">Consent Record</h2>
          {consent ? (
            <div className="space-y-2 text-sm">
              <p className="text-white/70">Signed: <span className="text-white">{new Date(consent.signed_at).toLocaleString()}</span></p>
              <p className="text-white/70">Version: <span className="text-white">{consent.consent_version}</span></p>
              <p className="text-white/70">IP: <span className="text-white">{consent.ip_address}</span></p>
              <p className="text-white/70">Signature: <span className="text-white italic">"{consent.signature_text}"</span></p>
            </div>
          ) : (
            <p className="text-white/40 text-sm">No consent record</p>
          )}
        </div>

        {/* Feedback */}
        <div className="bg-[#0E1830] border border-white/10 rounded-xl p-6">
          <h2 className="text-white font-medium mb-4">Feedback Response</h2>
          {feedback ? (
            <div className="space-y-2 text-sm">
              <p className="text-white/70">Value Score: <span className="text-white font-bold">{feedback.value_score}/10</span></p>
              <p className="text-white/70">Would Miss: <span className="text-white">{feedback.would_miss}</span></p>
              <p className="text-white/70">Would Recommend: <span className="text-white">{feedback.would_recommend}</span></p>
              <p className="text-white/70">User Type: <span className="text-white">{feedback.user_type}</span></p>
              {feedback.likes_most && <p className="text-white/50 text-xs mt-2">Likes: {feedback.likes_most}</p>}
              {feedback.frustrations && <p className="text-red-400/70 text-xs mt-1">Frustrations: {feedback.frustrations}</p>}
            </div>
          ) : (
            <p className="text-white/40 text-sm">No feedback submitted</p>
          )}
        </div>
      </div>

      {/* Admin Notes */}
      <div className="bg-[#0E1830] border border-white/10 rounded-xl p-6">
        <h2 className="text-white font-medium mb-4">Admin Notes</h2>
        <div className="flex gap-2 mb-4">
          <Input value={noteText} onChange={(e) => setNoteText(e.target.value)}
            placeholder="Add a note about this user..."
            className="bg-white/5 border-white/10 text-white placeholder:text-white/25" data-testid="note-input" />
          <Button onClick={handleAddNote} disabled={savingNote || !noteText.trim()}
            className="bg-gradient-to-r from-cyan-500 to-purple-500 text-white border-0" data-testid="add-note-btn">
            <Plus className="w-4 h-4" />
          </Button>
        </div>
        <div className="space-y-3 max-h-60 overflow-y-auto">
          {(notes || []).map((n) => (
            <div key={`${n.created_at}-${n.author_admin_email}`} className="bg-white/5 rounded-lg p-3">
              <p className="text-white text-sm">{n.note}</p>
              <p className="text-white/30 text-xs mt-1">{n.author_admin_email} &middot; {new Date(n.created_at).toLocaleString()}</p>
            </div>
          ))}
          {(!notes || notes.length === 0) && <p className="text-white/30 text-sm">No notes yet</p>}
        </div>
      </div>

      {/* Recent Activity */}
      <div className="bg-[#0E1830] border border-white/10 rounded-xl p-6">
        <h2 className="text-white font-medium mb-4">Recent Activity ({events?.length || 0} events)</h2>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-white/10">
                <th className="text-left px-3 py-2 text-white/40">Date</th>
                <th className="text-left px-3 py-2 text-white/40">Action</th>
                <th className="text-left px-3 py-2 text-white/40">Tone</th>
                <th className="text-left px-3 py-2 text-white/40">Intent</th>
                <th className="text-right px-3 py-2 text-white/40">Credits</th>
              </tr>
            </thead>
            <tbody>
              {(events || []).slice(0, 25).map((ev) => (
                <tr key={ev.created_at || ev.action} className="border-b border-white/5 hover:bg-white/5">
                  <td className="px-3 py-2 text-white/50">{ev.created_at ? new Date(ev.created_at).toLocaleString() : '-'}</td>
                  <td className="px-3 py-2 text-white">{ev.action || '-'}</td>
                  <td className="px-3 py-2 text-white/70">{ev.tone || '-'}</td>
                  <td className="px-3 py-2 text-white/70">{ev.intent || '-'}</td>
                  <td className="px-3 py-2 text-right text-white/50">{ev.credits_used || 0}</td>
                </tr>
              ))}
              {(!events || events.length === 0) && (
                <tr><td colSpan={5} className="text-center py-8 text-white/30">No activity yet</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Tickets */}
      <div className="bg-[#0E1830] border border-white/10 rounded-xl p-6">
        <h2 className="text-white font-medium mb-4">Support Tickets ({tickets?.length || 0})</h2>
        <div className="space-y-2">
          {(tickets || []).slice(0, 10).map((t) => (
            <div key={t.id || t.subject} className="flex items-center justify-between bg-white/5 rounded-lg p-3">
              <div>
                <p className="text-white text-sm">{t.subject}</p>
                <p className="text-white/30 text-xs">{t.category} &middot; {new Date(t.created_at).toLocaleDateString()}</p>
              </div>
              <span className={`px-2 py-0.5 text-xs rounded-full ${
                t.status === 'open' ? 'bg-blue-500/20 text-blue-400' :
                t.status === 'resolved' ? 'bg-emerald-500/20 text-emerald-400' :
                'bg-amber-500/20 text-amber-400'
              }`}>{t.status}</span>
            </div>
          ))}
          {(!tickets || tickets.length === 0) && <p className="text-white/30 text-sm">No tickets</p>}
        </div>
      </div>
    </div>
  );
}
